import React, { useEffect, useState } from 'react';
import {
  Box,
  Avatar,
  InputAdornment,
  Divider,
  Typography,
  TextField,
  Tooltip,
  IconButton,
  ListItemText,
  ListItemButton,
  Collapse,
} from '@mui/material';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';

import Iconify from '../../components/iconify';
import Scrollbar from '../../components/scrollbar';
import {
  RoleSideBar,
  CategorySideBar,
  ChurchSideBar,
  DistrictSideBar,
  PersonalSideBar,
} from '../../sections/chat/sideBar';
import axiosInstance from '../../api';
import SERVER_API_URL from '../../config';
import OutsideClickHandler from './OutsideClickHandler';

const StyledAccount = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  padding: 5,
  overflow: 'hidden',
}));

const StyledBox = styled(Box)(({ theme }) => ({
  pr: 1,
  width: '70%',
  height: '100%',
  position: 'absolute',
  top: 0,
  left: 0,
  zIndex: 5,
  backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#525657',
}));

export default function XSSidebar({
  user,
  targetType,
  targetId,
  roleQuery,
  handleSearch,
  categoryQuery,
  churchQuery,
  districtQuery,
  setRoleQuery,
  setCategoryQuery,
  setChurchQuery,
  setDistrictQuery,
  containerWidth,
  userQuery,
  setUserQuery,
  openSideBar,
  setOpenSideBar,
}) {
  const [avatar, setAvatar] = useState();

  const canReadPublicChat = user?.role?.permissions?.publicChat?.read;
  const canReadDistrictChat = user?.role?.permissions?.allDistrictChat?.read;
  const canReadChurchChat = user?.role?.permissions?.allChurchChat?.read;
  const canReadRoleChat = user?.role?.permissions?.allRoleChat?.read;
  const canReadCategoryChat = user?.role?.permissions?.allCategoryChat?.read;
  const canReadOwnDistrictChat = user?.role?.permissions?.ownDistrictChat?.read;
  const canReadOwnChurchChat = user?.role?.permissions?.ownChurchChat?.read;
  const canReadOwnRoleChat = user?.role?.permissions?.ownRoleChat?.read;
  const canReadOwnCategoryChat = user?.role?.permissions?.ownCategoryChat?.read;
  const canReadPersonalChat = user?.role?.permissions?.personalChat?.read;

  useEffect(() => {
    const getAvatar = async () => {
      try {
        const response = await axiosInstance.get(`${SERVER_API_URL}/avatar/getAvatarById/${user.id}`, {
          responseType: 'blob',
        });
        if (response.status === 200) {
          const url = URL.createObjectURL(response.data);
          setAvatar(url);
        }
      } catch (e) {
        setAvatar(null);
      }
    };
    getAvatar();
  }, [user]);

  return (
    <OutsideClickHandler onOutsideClick={() => setOpenSideBar(false)}>
      <Collapse
        in={openSideBar}
        timeout={{
          enter: 300,
          exit: 200,
        }}
        unmountOnExit
      >
        <StyledBox sx={{ display: { xs: openSideBar ? 'block' : 'none', sm: 'none' } }}>
          {((canReadPublicChat && targetType === 'public') ||
            (canReadPersonalChat && targetType === 'personal') ||
            ((canReadRoleChat || canReadOwnRoleChat) && targetType === 'role') ||
            ((canReadDistrictChat || canReadOwnDistrictChat) && targetType === 'district') ||
            ((canReadChurchChat || canReadOwnChurchChat) && targetType === 'church') ||
            ((canReadCategoryChat || canReadOwnCategoryChat) && targetType === 'category')) && (
              <Box>
                <StyledAccount>
                  <Tooltip
                    sx={{ display: { xs: 'none', sm: 'block' } }}
                    title={containerWidth === 'lg' ? 'ስፋትን ከፍ አድርግ' : 'ስፋትን ይቀንስ'}
                  >
                    <IconButton onClick={() => setOpenSideBar(!openSideBar)}>
                      <Avatar src={`${avatar}`} alt={`${user && user.firstName} አምሳያ`} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip sx={{ display: { xs: 'block', sm: 'none' } }} title={containerWidth === 'lg' ? 'open' : 'close'}>
                    <IconButton onClick={() => setOpenSideBar(!openSideBar)}>
                      <Avatar src={`${avatar}`} alt={`${user && user.firstName} አምሳያ`} />
                    </IconButton>
                  </Tooltip>
                  <Box sx={{ ml: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                      {user && user.firstName} {user && user.lastName}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {user && user.username}
                    </Typography>
                  </Box>
                </StyledAccount>

                <Divider sx={{ borderStyle: 'dashed' }} />
                <Box sx={{ p: { xs: 0.5, lg: 1 }, border: 0.5, borderColor: 'divider', mt: { xs: 0.25, lg: 0.5 } }}>
                  {targetType !== 'public' && (
                    <Box
                      sx={{
                        my: 0.5,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        alignItems: 'end',
                        position: 'sticky',
                        bottom: 0,
                      }}
                    >
                      <TextField
                        fullWidth
                        disableUnderline
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled', width: 20, height: 20 }} />
                            </InputAdornment>
                          ),
                        }}
                        size="small"
                        type="search"
                        placeholder="ፍለጋ... "
                        onChange={(e) => {
                          if (targetType === 'personal') {
                            setUserQuery(e.target.value);
                          } else if (targetType === 'district') {
                            setDistrictQuery(e.target.value);
                          } else if (targetType === 'church') {
                            setChurchQuery(e.target.value);
                          } else if (targetType === 'role') {
                            setRoleQuery(e.target.value);
                          } else if (targetType === 'category') {
                            setCategoryQuery(e.target.value);
                          }
                        }}
                        sx={{ flexGrow: 1 }}
                      />
                    </Box>
                  )}

                  <Divider sx={{ borderStyle: 'dashed' }} />

                  <Scrollbar
                    sx={{
                      height: 'calc(100vh - 200px)',
                      overflow: 'auto',
                    }}
                  >
                    {targetType === 'public' && (
                      <>
                        <ListItemButton
                          sx={{
                            py: 1.5,
                            px: 2.5,
                            mt: '1px',
                            ...{
                              bgcolor: 'action.selected',
                            },
                          }}
                        >
                          <ListItemText primary={`የህዝብ መወያያ`} />
                        </ListItemButton>
                      </>
                    )}

                    {targetType === 'personal' && (
                      <PersonalSideBar
                        userQuery={userQuery}
                        handleSearch={handleSearch}
                        targetId={targetId}
                        myId={user && user.id}
                      />
                    )}

                    {targetType === 'district' && (
                      <>
                        {canReadDistrictChat ? (
                          <DistrictSideBar
                            districtQuery={districtQuery}
                            handleSearch={handleSearch}
                            targetId={targetId}
                            userId={user && user.id}
                          />
                        ) : (
                          <>
                            <ListItemButton
                              sx={{
                                py: 1.5,
                                px: 2.5,
                                mt: '1px',
                                ...{
                                  bgcolor: 'action.selected',
                                },
                              }}
                            >
                              <ListItemText primary={user && user.church.district.name} />
                            </ListItemButton>
                          </>
                        )}
                      </>
                    )}

                    {targetType === 'church' && (
                      <>
                        {canReadChurchChat ? (
                          <ChurchSideBar
                            churchQuery={churchQuery}
                            handleSearch={handleSearch}
                            targetId={targetId}
                            userId={user && user.id}
                          />
                        ) : (
                          <>
                            <ListItemButton
                              sx={{
                                py: 1.5,
                                px: 2.5,
                                mt: '1px',
                                ...{
                                  bgcolor: 'action.selected',
                                },
                              }}
                            >
                              <ListItemText primary={user && user.church.name} />
                            </ListItemButton>
                          </>
                        )}
                      </>
                    )}

                    {targetType === 'category' && (
                      <>
                        {canReadCategoryChat ? (
                          <CategorySideBar
                            categoryQuery={categoryQuery}
                            handleSearch={handleSearch}
                            targetId={targetId}
                            userId={user?.id}
                          />
                        ) : (
                          <ListItemButton
                            sx={{
                              py: 1.5,
                              px: 2.5,
                              mt: '1px',
                              bgcolor: 'action.selected',
                            }}
                          >
                            <ListItemText primary={user?.category.name} />
                          </ListItemButton>
                        )}
                      </>
                    )}

                    {targetType === 'role' && (
                      <>
                        {canReadRoleChat ? (
                          <RoleSideBar
                            roleQuery={roleQuery}
                            handleSearch={handleSearch}
                            targetId={targetId}
                            userId={user && user.id}
                          />
                        ) : (
                          <>
                            <ListItemButton
                              sx={{
                                py: 1.5,
                                px: 2.5,
                                mt: '1px',
                                ...{
                                  bgcolor: 'action.selected',
                                },
                              }}
                            >
                              <ListItemText primary={user && user.role.name} />
                            </ListItemButton>
                          </>
                        )}
                      </>
                    )}
                  </Scrollbar>
                </Box>
              </Box>
            )}
        </StyledBox>
      </Collapse>
    </OutsideClickHandler>
  );
}

XSSidebar.propTypes = {
  user: PropTypes.object.isRequired,
  targetType: PropTypes.string.isRequired,
  targetId: PropTypes.string.isRequired,
  roleQuery: PropTypes.string.isRequired,
  handleSearch: PropTypes.func.isRequired,
  categoryQuery: PropTypes.string.isRequired,
  churchQuery: PropTypes.string.isRequired,
  districtQuery: PropTypes.string.isRequired,
  setRoleQuery: PropTypes.func.isRequired,
  setCategoryQuery: PropTypes.func.isRequired,
  setChurchQuery: PropTypes.func.isRequired,
  setDistrictQuery: PropTypes.func.isRequired,
  containerWidth: PropTypes.number.isRequired,
  userQuery: PropTypes.string.isRequired,
  setUserQuery: PropTypes.func.isRequired,
  openSideBar: PropTypes.bool.isRequired,
  setOpenSideBar: PropTypes.func.isRequired,
};
